import { ReactNode, useRef } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ICareMapNoteTarget } from '../../notes/model/ICareMapNoteTarget';
import { RecordingState } from '../RecordingState';
import { RevenueCatModel } from '../../innerhive-plus/revenue-cat/RevenueCatModel';
import { InnerhivePlusDialogViewModel } from '../../innerhive-plus/InnerhivePlusDialogViewModel';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { useToast } from '../../components/useToast';
import { AudioUploadService, IAudioUploadRejection } from './AudioUploadService';
import { AUDIO_UPLOAD_ACCEPT, getExtensionFromFileName, IAudioRejectionReason, MAX_AUDIO_FILE_BYTES } from './AudioFileValidator';

const reasonText = (reason: IAudioRejectionReason): string => {
    if (reason === 'too-large') {
        return `is over ${Math.round(MAX_AUDIO_FILE_BYTES / (1024 * 1024))} MB`;
    }
    if (reason === 'upload-failed') {
        return 'couldn\'t be uploaded';
    }
    return 'isn\'t a supported audio file';
};

// Name the offending file(s) so the toast is actionable (the picker normally prevents this,
// so it's an edge case — but when it happens the user should know which file and why).
const rejectionMessage = (rejected: IAudioUploadRejection[]): string => {
    if (rejected.length === 1) {
        return `"${rejected[0].fileName}" ${reasonText(rejected[0].reason)}.`;
    }
    return `Couldn't add ${rejected.length} files: ${rejected.map(r => `"${r.fileName}" (${reasonText(r.reason)})`).join(', ')}.`;
};

/**
 * Drives the "Upload audio" flow: paywall → file picker → create a note per file (via
 * AudioUploadService, which routes through the recording pipeline). No consent step — that's
 * for live recording of other people; uploading a file you already have doesn't need it.
 *
 * Returns an imperative `startUpload` plus a `uploadUI` node (hidden file input) the caller renders.
 */
export const useUploadAudio = ({ noteTarget, onFilesSelected }: { noteTarget?: ICareMapNoteTarget; onFilesSelected?: () => void } = {}): { startUpload: () => Promise<void>; uploadUI: ReactNode } => {
    const audioUploadService = useInstance(AudioUploadService);
    const recordingState = useInstance(RecordingState);
    const revenueCat = useInstance(RevenueCatModel);
    const ihPlusDialogVM = useInstance(InnerhivePlusDialogViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const [showToast] = useToast();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const ensureInnerhivePlus = async (): Promise<boolean> => {
        if (revenueCat.hasInnerhivePlus() || !recordingState.requiresInnerhivePlus(revenueCat.getFreeTierLimits())) {
            return true;
        }
        return ihPlusDialogVM.requirePurchase({ triggerSource: 'audio upload' });
    };

    const startUpload = async () => {
        if (!(await ensureInnerhivePlus())) {
            return;
        }
        fileInputRef.current?.click();
    };

    const onFilesPicked = async (files: File[]) => {
        if (files.length === 0) {
            return;
        }

        // A file was chosen, so the chooser has served its purpose — close it now (cancelling the
        // native picker fires no event, so the chooser stays open in that case, by design).
        onFilesSelected?.();

        const { addedCount, rejected } = await audioUploadService.uploadFiles(files, noteTarget);

        rejected.forEach(r => {
            mixpanel.track('Audio Upload Rejected', () => ({
                'Reason': r.reason,
                'Extension': getExtensionFromFileName(r.fileName) || 'unknown'
            }));
        });

        // Accepted files surface their own progress via the recording pipeline's processing modal,
        // so we don't toast on success (it would stack on top of that modal). Only surface a toast
        // when nothing could be added — there's no processing modal to collide with.
        if (addedCount === 0 && rejected.length) {
            showToast({ message: rejectionMessage(rejected), duration: 5000 });
        }
    };

    // Each chooser renders its own hidden input. Derive a unique, predictable data-id from the
    // target so two choosers in the DOM at once (e.g. a page + an overlaying notes drawer) don't
    // collide, and a test can address a specific chooser. Patient (the default) keeps the bare id.
    const uploadInputDataId = ['audio-upload-input', noteTarget?.section, noteTarget?.memberId].filter(Boolean).join('-');

    const uploadUI = (
        <input
            ref={fileInputRef}
            type="file"
            accept={AUDIO_UPLOAD_ACCEPT}
            hidden
            data-id={uploadInputDataId}
            onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                // reset so picking the same file again still fires onChange
                e.target.value = '';
                onFilesPicked(files);
            }}
        />
    );

    return { startUpload, uploadUI };
};
